import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clearAuthCallbackParams, readAuthCallbackError } from '../lib/authErrors';
import { LoginBrand } from '../components/auth/LoginBrand';
import { VerifyingOverlay } from '../components/ui/VerifyingOverlay';
import { formatAuthErrorMessage } from '../lib/formatAuthError';
import {
  IS_UNIFIED_PORTAL,
  isSoftPortalEntryPending,
  portalHomePath,
  portalKindFromPath,
  readPortalChoice,
  softPortalEntryTarget,
} from '../lib/portal';
import { resolveDefaultPortalKind } from '../lib/portalAccess';
import { syncMobileAppHeight } from '../lib/mobileViewport';

export function LoginPage() {
  const {
    session,
    configured,
    signInWithMicrosoft,
    loading,
    authError,
    clearAuthError,
    accessStatus,
    portalAccess,
  } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Match CRM/DEM shell height on mobile only — desktop login must stay full-viewport.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const apply = () => {
      if (!mq.matches) {
        document.documentElement.classList.remove('has-auth-shell');
        return;
      }
      document.documentElement.classList.add('has-auth-shell');
      syncMobileAppHeight();
    };
    apply();
    const onResize = () => apply();
    mq.addEventListener('change', apply);
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    return () => {
      document.documentElement.classList.remove('has-auth-shell');
      mq.removeEventListener('change', apply);
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    const callbackError = readAuthCallbackError();
    if (callbackError) {
      setError(formatAuthErrorMessage(callbackError));
      clearAuthCallbackParams();
    }
  }, []);

  useEffect(() => {
    if (authError) {
      setError(formatAuthErrorMessage(authError));
    }
  }, [authError]);

  // Soft choose→portal must never flash Login "Verifying" on mobile if auth
  // briefly bounced us here.
  if (isSoftPortalEntryPending()) {
    const kind = softPortalEntryTarget() ?? readPortalChoice();
    if (kind) return <Navigate to={portalHomePath(kind)} replace />;
  }

  if (session && accessStatus === 'choose_portal') {
    return <Navigate to="/choose-portal" replace />;
  }

  if (session && (accessStatus === 'not_on_allowlist' || accessStatus === 'no_email')) {
    return <Navigate to="/not-authorized" replace />;
  }

  if (session && accessStatus === 'allowed') {
    if (IS_UNIFIED_PORTAL) {
      const pathKind = portalKindFromPath(window.location.pathname);
      const choice = readPortalChoice();
      const target =
        pathKind ?? choice ?? (portalAccess ? resolveDefaultPortalKind(portalAccess) : null);
      if (target) {
        return <Navigate to={portalHomePath(target)} replace />;
      }
      return <Navigate to="/choose-portal" replace />;
    }
    return <Navigate to="/" replace />;
  }

  const showVerifying =
    loading || (Boolean(session) && (accessStatus === 'idle' || accessStatus === 'checking'));

  async function handleMicrosoftLogin() {
    setError(null);
    clearAuthError();
    setBusy(true);
    try {
      await signInWithMicrosoft();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Microsoft sign-in failed');
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <LoginBrand />

        <div className="login-card__body">
          <p className="login-card__lead">Sign in with your company Microsoft account</p>

          {!configured && (
            <div className="alert alert-warn">
              Copy <code>.env.example</code> to <code>.env</code> and set your Supabase URL and anon key.
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          <button
            type="button"
            className="btn-primary login-card__ms-btn"
            disabled={!configured || busy || showVerifying}
            onClick={() => void handleMicrosoftLogin()}
          >
            {busy ? 'Redirecting to Microsoft…' : 'Sign in with Microsoft'}
          </button>
        </div>
      </div>

      {showVerifying ? (
        <VerifyingOverlay message={session ? 'Verifying access…' : 'Loading…'} />
      ) : null}
    </div>
  );
}
